import React, { useEffect, useState, useRef, useCallback } from "react";

import { Box, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import AvatarForm from "@app/components/atoms/AvatarForm";
import Upload, { UploadRef } from "@app/components/atoms/Upload";
import { TAssetPath } from "@app/constants";

type UploadImageProps = {
  label: string;
  destination: TAssetPath;
  imageUrl?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  onUploadSuccess: (imageUrl?: string) => void;
  onRemoveImage: () => void;
};

const UploadImage = ({
  label,
  destination,
  imageUrl,
  error,
  errorMessage,
  disabled,
  onUploadSuccess,
  onRemoveImage,
}: UploadImageProps) => {
  const fileUploadRef = useRef<UploadRef>(null);

  const [imageUrlData, setImageUrlData] = useState(imageUrl);
  const [dataError, setDataError] = useState<{
    error: boolean;
    errorMessage: string;
  }>();

  const handleUploadSuccess = useCallback(
    (url: string) => {
      onUploadSuccess(url);
    },
    [onUploadSuccess]
  );

  const handleClearField = () => {
    setImageUrlData("");
    fileUploadRef.current?.clearField();
    onRemoveImage();
  };

  useEffect(() => {
    if (error && errorMessage) {
      setDataError({ error, errorMessage });
    }
  }, [error, errorMessage]);

  useEffect(() => {
    setImageUrlData(imageUrl);
  }, [imageUrl]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      {!!imageUrlData && <AvatarForm imageSrc={imageUrlData} />}
      {!disabled && !imageUrlData && (
        <Upload
          ref={fileUploadRef}
          destination={destination}
          accept={{ image: true }}
          disabled={disabled}
          label={label}
          error={dataError?.error}
          errorMessage={dataError?.errorMessage}
          onUploadSuccess={handleUploadSuccess}
        />
      )}
      {!!imageUrlData && !disabled && (
        <IconButton onClick={handleClearField}>
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );
};

UploadImage.defaultProps = {
  imageUrl: "",
  error: false,
};

export default UploadImage;
